import React, { useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import debounce from 'lodash/debounce'
import { change, reduxForm, SubmissionError } from 'redux-form'

import { useMediaQuery, pageLoad } from '@offerzen/pinata'

import featuredImage from '../../../assets/signup-featured.svg'

import ConfirmationForm from '../../components/ConfirmationForm'
import { clearPlacesData, findPlaces } from '../../redux/modules/places'
import {
  setLoading as reduxSetLoading,
  updateCandidate as reduxUpdateCandidate,
  confirmDetailsCandidate as reduxConfirmDetailsCandidate,
} from '../../redux/modules/candidate'
import { updateProgress as reduxUpdateProgress } from '../../redux/modules/utility'
import { firstOnboardingStepUrl } from '../../helpers/onboardingSteps'

import styles from './styles'

const SocialSignupConfirm = ({ places, candidate, searchCities, addCity, updateProgress, ...props }) => {
  const { isTabletOrLarger } = useMediaQuery()

  useEffect(() => {
    updateProgress(2)
    pageLoad()
  }, [updateProgress])

  const confirmDetails = async data => {
    if (!data.based_in_location.geoapify_place_id) {
      throw new SubmissionError({
        location: 'Please select a location from the suggestions',
      })
    }
    return props.confirmDetails(data)
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearchCitiesDebounce = useCallback(debounce(searchCities, 150), [])

  return (
    <div className={styles.container}>
      <Choose>
        <When condition={isTabletOrLarger}>
          <div className={styles.columnContainer}>
            <div>
              <h1>Make the best move of your developer career</h1>
              <ul className={styles.infoList}>
                <li>Get seen by over 2000 legit tech companies</li>
                <li>Receive upfront role and salary info when companies reach out</li>
                <li>Smash job interviews with your dedicated dev career coach</li>
              </ul>
              <img alt="featured" className={styles.featuredImage} src={featuredImage} />
            </div>
            <div>
              <div className={styles.card}>
                <div className={styles.headerSection}>
                  <h3>Confirm your details</h3>
                </div>
                <ConfirmationForm
                  onSubmit={props.handleSubmit(confirmDetails)}
                  places={places}
                  candidate={candidate}
                  addCity={addCity}
                  searchCities={term => handleSearchCitiesDebounce(term)}
                  clearData={props.clearData}
                  clearPlace={props.clearPlace}
                  loading={props.loading}
                />
              </div>
            </div>
          </div>
        </When>
        <Otherwise>
          <div className={styles.mobileContainer}>
            <div className={styles.card}>
              <div className={styles.headerSection}>
                <h3>Confirm your details</h3>
              </div>
              <ConfirmationForm
                onSubmit={props.handleSubmit(confirmDetails)}
                places={places}
                candidate={candidate}
                addCity={addCity}
                searchCities={term => handleSearchCitiesDebounce(term)}
                clearData={props.clearData}
                clearPlace={props.clearPlace}
                loading={props.loading}
              />
            </div>
          </div>
        </Otherwise>
      </Choose>
    </div>
  )
}

SocialSignupConfirm.propTypes = {
  candidate: PropTypes.shape({}),
  places: PropTypes.shape({}),
  initialValues: PropTypes.shape({}),
  updateProgress: PropTypes.func,
  setLoading: PropTypes.func,
  location: PropTypes.shape({}),
  addCity: PropTypes.func,
  searchCities: PropTypes.func,
  clearData: PropTypes.func,
  clearPlace: PropTypes.func,
  handleSubmit: PropTypes.func,
  confirmDetails: PropTypes.func,
  loading: PropTypes.bool,
  addCityGooglePlaces: PropTypes.func,
}

const mapStateToProps = state => ({
  loading: state.candidate ? state.candidate.loading : false,
  candidate: state.candidate,
  places: state.places,
  initialValues: {
    email: state.candidate.email,
    first_name: state.candidate.first_name,
    last_name: state.candidate.last_name,
  },
})

const mapDispatchToProps = dispatch => ({
  searchCities: term => dispatch(findPlaces(term)),
  clearPlace: () => {
    dispatch(change('socialSignupConfirm', 'based_in_location', {}))
  },
  addCity: value => {
    dispatch(change('socialSignupConfirm', 'location', value.name))
    dispatch(change('socialSignupConfirm', 'based_in_location', value.based_in_location))
  },

  clearData: () => dispatch(clearPlacesData()),
  setLoading: () => dispatch(reduxSetLoading()),
  updateProgress: progress => dispatch(reduxUpdateProgress(progress)),
  updateCandidate: (candidate, nextRoute) => dispatch(reduxUpdateCandidate(candidate, nextRoute)),
  confirmDetails: (candidate, url) => dispatch(reduxConfirmDetailsCandidate(candidate, url)),
})

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...stateProps,
  ...dispatchProps,
  ...ownProps,
  confirmDetails: candidate => {
    const url = firstOnboardingStepUrl(stateProps.candidate.slug)
    dispatchProps.confirmDetails(candidate, url)
  },
})

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps,
    mergeProps,
  )(
    reduxForm({
      form: 'socialSignupConfirm',
    })(SocialSignupConfirm),
  ),
)
